<template>
  <div>
    <v-btn
      class="border-0 cursor-grab sortHandle"
      size="small"
      variant="plain"
      tabindex="-1"
      icon="mds:drag-indicator"
      :disabled='store.state.isWorkflowDisabled'
    />
    <quick-rename-input
      :id="`edit-entity-${entity.id}-field-${index}`"
      class="flex-grow-1"
      :value="field.name"
      :density="`compact`"
      :max-length="30"
      @onChange="blurTableCell($event, entity.id, index, 'name', null)"
      :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
      :disabled="store.state.isWorkflowDisabled"
    />
    <span v-if="dataTypeLabel" class="data-type-label">{{ dataTypeLabel }}</span>
    <v-chip
      v-if="field.relatedEntityId && relatedEntity"
      density="compact"
      size="small"
      :closable="!store.state.isWorkflowDisabled"
      class="related-entity-chip px-2 ml-1"
    >
      {{ field.cardinality === 'one-to-many'
        ? `[ ${getRelatedEntity(field)?.name} ]`
        : `${getRelatedEntity(field)?.name}` }}
      <template v-if="!store.state.isWorkflowDisabled" #close>
        <v-icon
          icon="mdi-close-circle"
          color="#888"
          :size="16"
          class="remove-entity-icon"
          @click.stop="selectRelatedEntity(null, entity.id, index)"
        />
      </template>
    </v-chip>
    <v-menu
      :model-value="openMenu"
      @update:modelValue="() => null"
      class="card-menu"
      @click:outside="openMenu = false"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          icon="mdi-dots-vertical"
          size="small"
          variant="plain"
          v-bind="props"
          @click="openMenu = !openMenu"
        />
      </template>
      <v-list density="compact">
        <select-entity
          @onSelectEntity="(selectedEntity) => selectRelatedEntity(selectedEntity, entity.id, index)"
        >
          <v-list-item class="cursor-pointer">
            <v-list-item-title>
              <v-icon class="mr-2" color="grey">mdi-database</v-icon> Select Entity
            </v-list-item-title>
          </v-list-item>
        </select-entity>
        <v-list-item class="cursor-pointer">
          <v-list-item-title class="d-flex align-center">
            <v-icon class="mr-2" color="grey" icon="mdi-swap-horizontal" />
            Change data type
            <v-icon size="16" class="ml-auto text-grey">mdi-chevron-right</v-icon>
          </v-list-item-title>
          <v-menu
            activator="parent"
            open-on-hover
            location="end"
            offset="4"
          >
            <v-list density="compact" :selected="[field.dataType || 'string']" select-strategy="leaf" active-class="text-dark">
              <v-list-item value="string" class="cursor-pointer" @click="handleChangeDataType('string')">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" icon="mdi-format-letter-case" size="18" /> String
                </v-list-item-title>
              </v-list-item>
              <v-list-item value="number" class="cursor-pointer" @click="handleChangeDataType('number')">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" icon="mdi-numeric" size="18" /> Number
                </v-list-item-title>
              </v-list-item>
              <v-list-item value="boolean" class="cursor-pointer" @click="handleChangeDataType('boolean')">
                <v-list-item-title>
                  <v-icon class="mr-2" color="grey" icon="mdi-toggle-switch" size="18" /> Boolean
                </v-list-item-title>
              </v-list-item>
              <v-list-item value="object" class="cursor-pointer">
                <v-list-item-title class="d-flex align-center">
                  <v-icon class="mr-2" color="grey" icon="mdi-code-json" size="18" /> Object
                  <v-icon size="14" class="ml-auto text-grey">mdi-chevron-right</v-icon>
                </v-list-item-title>
                <v-menu
                  activator="parent"
                  open-on-hover
                  location="end"
                  offset="4"
                >
                  <v-list
                    density="compact"
                    :selected="field.dataType === 'object' && field.relatedEntityId ? [field.relatedEntityId] : []"
                    select-strategy="leaf"
                    active-class="text-dark">
                    <v-list-item disabled class="select-entity-header">
                      <v-list-item-title>
                        <v-icon class="mr-2" color="grey" icon="mdi-database" size="18" /> Select Entity
                      </v-list-item-title>
                    </v-list-item>
                    <v-divider class="mb-1" />
                    <v-list-item
                      v-for="entityOption in selectableEntities"
                      :key="entityOption.id"
                      :value="entityOption.id"
                      class="cursor-pointer"
                      @click="handleChangeDataType('object', entityOption)"
                    >
                      <v-list-item-title>{{ entityOption.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
        <v-list-item @click="setFieldMandatory(entity.id, index, !(field.tags && field.tags.includes('REQUIRED')))">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" icon="mdi-asterisk" />
            {{ field.tags && field.tags.includes('REQUIRED') ? 'Set as optional' : 'Set as required' }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="deleteEntityField(entity.id, index)">
          <v-list-item-title>
            <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" /> Delete
          </v-list-item-title>
        </v-list-item>
        <v-divider></v-divider>
        <div class="d-flex justify-center pt-1">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <toggle
                v-bind="!field.relatedEntityId ? props : undefined"
                mandatory
                :value="field.cardinality || 'one-to-one'"
                :options="FIELD_CARDINALITY_OPTIONS"
                :disabled="store.state.isWorkflowDisabled || !field.relatedEntityId"
                @update:modelValue="(cardinality) => selectCardinality(cardinality, entity.id, field.relatedEntityId)"
              />
            </template>
            <span>Disabled because this field is not set to be a related entity.</span>
          </v-tooltip>
        </div>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, ref } from 'vue'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import useDomainModel from '@/composables/useDomainModel'
import useEntityActions from '@/composables/useEntityActions'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import { SchemaType, DataTypeShortLabels, FIELD_CARDINALITY_OPTIONS } from '@/constants/schema'

const { entity, field, index } = defineProps({ entity: Object, field: Object, index: Number })

const openMenu = ref(false)

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const relatedEntity = computed(() => getRelatedEntity(field))
const dataTypeLabel = computed(() => DataTypeShortLabels[field.dataType] || '')

const selectableEntities = computed(() => {
  return (workflow.value.schemas ?? []).filter((schema) => schema.type === SchemaType.ENTITY)
})

const {
  blurTableCell,
  setDataType,
  deleteEntityField,
  selectRelatedEntity,
  selectCardinality,
  setFieldMandatory
} = useEntityActions()

const { getRelatedEntity } = useDomainModel()

const handleChangeDataType = (dataType, entityRef) => {
  openMenu.value = false
  if (dataType === 'object') {
    if (entityRef) {
      setDataType(entity.id, index, dataType, entityRef.id)
    }
  } else {
    setDataType(entity.id, index, dataType, null)
  }
}
</script>

<style scoped>
.card-menu .v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.select-entity-header {
  opacity: 1 !important;
  pointer-events: none;
}

.required-field {
  &::after {
    content: ' *';
  }

  &:focus::after {
    visibility: hidden;
  }
}

.data-type-label {
  font-size: 10px;
  color: #9e9e9e;
  font-weight: 500;
  margin-left: 2px;
  user-select: none;
  flex-shrink: 0;
}

:deep(.related-entity-chip) {
  position: relative;

  &&& .v-chip__close {
    margin-inline-start: 0;
    margin-inline-end: 0;
    position: absolute;
    right: 0;
    background: #e8e8e8;
    opacity: 0;
    transition: all 0.2s;
  }

  &:hover {
    &&& .v-chip__close {
      opacity: 1;
    }
  }
}
</style>
